import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { getOverlappingSeasonalStrategies } from 'lumen/utils/pricing-assistant/pms-integration/seasonal-room-hierarchy';
import { safeRollback } from 'lumen/utils/pricing-assistant/recommendation-settings';

import styles from './seasonal-strategy-card.module.css';

interface SettingsPmsIntegrationSeasonalStrategyCardSignature {
  Args: {
    isEditing: boolean;
    isReadOnly: boolean;
    onCancel: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onSaved: (seasonalStrategy: RecommendationStrategy) => void;
    // All seasonal strategies, used to detect overlaps. This one is excluded
    // internally.
    seasonalStrategies: RecommendationStrategy[];
    strategy: RecommendationStrategy;
  };
  Element: HTMLDivElement;
}

export default class SettingsPmsIntegrationSeasonalStrategyCard extends Component<SettingsPmsIntegrationSeasonalStrategyCardSignature> {
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  // Dates are kept locally while editing so we don't dirty the record (or reorder
  // the parent list) until save. Snapshotted from the record on edit-entry.
  @tracked componentSeasonEndDt: Dayjs | null = null;
  @tracked componentSeasonStartDt: Dayjs | null = null;
  // Overlap error/warning states only surface once the user edits the range, so
  // opening an existing strategy doesn't immediately flag it.
  @tracked isDateRangeChanged = false;
  @tracked isOpen = false;
  @tracked saveAttempted = false;

  saveSeasonalStrategyTask = task({ drop: true }, async (): Promise<void> => {
    this.saveAttempted = true;

    if (this.hasValidationErrors) {
      return;
    }

    const { strategy } = this.args;
    strategy.seasonStartDt = this.componentSeasonStartDt;
    strategy.seasonEndDt = this.componentSeasonEndDt;

    try {
      await strategy.save();
    } catch {
      await this.alerting.showErrorAlert();
      return;
    }

    // Leave a clean edit state behind (mirrors StrategyCard); the card returns to
    // read mode via onSaved.
    this.saveAttempted = false;
    this.isDateRangeChanged = false;
    this.args.onSaved(strategy);
  });

  get dateRangeTitle(): string {
    const { seasonEndDt, seasonStartDt } = this.args.strategy;

    if (!seasonStartDt || !seasonEndDt) {
      return '';
    }

    if (seasonStartDt.isSame(seasonEndDt, 'day')) {
      return seasonStartDt.format('DD MMM YYYY');
    }

    return `${seasonStartDt.format('DD MMM YYYY')} - ${seasonEndDt.format(
      'DD MMM YYYY',
    )}`;
  }

  // An identical date range is a hard conflict and blocks saving — mirrors the
  // recommendation-settings hierarchy editor (exact overlap = error).
  get exactlyOverlappingSeasonalStrategy(): RecommendationStrategy | undefined {
    const { componentSeasonEndDt: end, componentSeasonStartDt: start } = this;

    if (!start || !end) {
      return undefined;
    }

    return this.overlappingSeasonalStrategies.find(
      (seasonalStrategy) =>
        Boolean(seasonalStrategy.seasonStartDt?.isSame(start, 'day')) &&
        Boolean(seasonalStrategy.seasonEndDt?.isSame(end, 'day')),
    );
  }

  get hasDateRange(): boolean {
    return Boolean(this.componentSeasonStartDt && this.componentSeasonEndDt);
  }

  get hasExactOverlap(): boolean {
    return Boolean(this.exactlyOverlappingSeasonalStrategy);
  }

  // A partial (non-identical) overlap is allowed but warns: the newer strategy
  // takes precedence on the overlapping days. Only warn when the overlapped
  // strategy is older than this one (a not-yet-saved strategy has no createdAt
  // and is treated as the newest).
  get hasPartialOverlapWarning(): boolean {
    const editedCreatedAt = this.args.strategy.createdAt;

    return this.partiallyOverlappingSeasonalStrategies.some(
      (seasonalStrategy) =>
        editedCreatedAt
          ? Boolean(seasonalStrategy.createdAt?.isBefore(editedCreatedAt))
          : true,
    );
  }

  get hasValidationErrors(): boolean {
    return (
      !this.isNameValid ||
      this.isNameUsed ||
      !this.hasDateRange ||
      this.hasExactOverlap
    );
  }

  get hasValidationMessages(): boolean {
    if (!this.saveAttempted) {
      return false;
    }

    return (
      !this.isNameValid ||
      this.isNameUsed ||
      this.hasExactOverlap ||
      (this.hasPartialOverlapWarning && this.isDateRangeChanged)
    );
  }

  get isDatepickerTouched(): boolean {
    // New strategies start without a date range (which is required), so we only
    // surface the error state once the user has attempted to save.
    return !this.args.strategy.isNew || this.saveAttempted;
  }

  // Blocks saving a strategy whose name duplicates another — mirrors the
  // recommendation-settings StrategyCard (isStrategyNameValid).
  get isNameUsed(): boolean {
    const name = this.args.strategy.name?.trim();

    if (!name) {
      return false;
    }

    return this.args.seasonalStrategies.some(
      (seasonalStrategy) =>
        seasonalStrategy !== this.args.strategy &&
        seasonalStrategy.name?.trim() === name,
    );
  }

  get isNameValid(): boolean {
    return (this.args.strategy.name?.trim().length ?? 0) > 0;
  }

  // True when a newer seasonal strategy overlaps this one and therefore takes
  // precedence on the shared days — mirrors StrategyCard's "Overlapping days" badge.
  get isOverlapped(): boolean {
    const { seasonalStrategies, strategy } = this.args;
    const { createdAt } = strategy;

    if (!createdAt) {
      return false;
    }

    const otherSeasonalStrategies = seasonalStrategies.filter(
      (seasonalStrategy) => seasonalStrategy !== strategy,
    );

    return getOverlappingSeasonalStrategies(
      strategy.seasonStartDt,
      strategy.seasonEndDt,
      otherSeasonalStrategies,
    ).some((seasonalStrategy) =>
      Boolean(seasonalStrategy.createdAt?.isAfter(createdAt)),
    );
  }

  get maxAllowedDatepickerEndDate(): Dayjs {
    return this.hotelAndMarketService
      .getLocalTime()
      .endOf('day')
      .add(3, 'years');
  }

  get minAllowedDatepickerEndDate(): Dayjs {
    return this.componentSeasonStartDt ?? this.minAllowedDatepickerStartDate;
  }

  get minAllowedDatepickerStartDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime().startOf('day');
  }

  // Computed once per change (via @cached) — the exact/partial getters below all
  // read it, so this avoids recomputing the overlap set several times per render.
  @cached
  get overlappingSeasonalStrategies(): RecommendationStrategy[] {
    const otherSeasonalStrategies = this.args.seasonalStrategies.filter(
      (seasonalStrategy) => seasonalStrategy !== this.args.strategy,
    );

    return getOverlappingSeasonalStrategies(
      this.componentSeasonStartDt,
      this.componentSeasonEndDt,
      otherSeasonalStrategies,
    );
  }

  get partiallyOverlappingSeasonalStrategies(): RecommendationStrategy[] {
    return this.overlappingSeasonalStrategies.filter(
      (seasonalStrategy) =>
        seasonalStrategy !== this.exactlyOverlappingSeasonalStrategy,
    );
  }

  constructor(
    owner: Owner,
    args: SettingsPmsIntegrationSeasonalStrategyCardSignature['Args'],
  ) {
    super(owner, args);

    // Seed the local range from the record; it's re-synced on every edit exit
    // (save/cancel), so re-entering the editor never shows a stale edit.
    this.componentSeasonStartDt = this.args.strategy.seasonStartDt;
    this.componentSeasonEndDt = this.args.strategy.seasonEndDt;
  }

  // Closing the modal tears the card down: discard any unsaved changes.
  // safeRollback reverts an edited strategy and removes an unsaved new one.
  willDestroy(): void {
    super.willDestroy();

    safeRollback(this.args.strategy);
  }

  @action
  edit(): void {
    // The parent flips us into edit mode; local edit state is seeded in the
    // constructor and reset on every exit (save/cancel), so nothing to do here.
    this.args.onEdit();
  }

  @action
  onCancel(): void {
    const wasNew = this.args.strategy.isNew;

    // Notify the parent before rolling back, so it can still read the record's
    // state (e.g. isNew) before it's unloaded.
    this.args.onCancel();
    safeRollback(this.args.strategy);

    // An existing card stays mounted and returns to read mode: re-sync the local
    // range to the rolled-back record and clear validation (mirrors StrategyCard).
    // A new one has been unloaded, so there's nothing to sync.
    if (!wasNew) {
      this.componentSeasonStartDt = this.args.strategy.seasonStartDt;
      this.componentSeasonEndDt = this.args.strategy.seasonEndDt;
      this.isDateRangeChanged = false;
      this.saveAttempted = false;
    }
  }

  @action
  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  updateEndDate(value: Dayjs | null): void {
    this.componentSeasonEndDt = value
      ? this.hotelAndMarketService.toLocalTime(value.format('YYYY-MM-DD'))
      : null;
    this.isDateRangeChanged = true;
  }

  @action
  updateName(event: Event): void {
    this.args.strategy.name = (event.target as HTMLInputElement).value;
  }

  @action
  updateStartDate(value: Dayjs | null): void {
    if (value) {
      this.componentSeasonStartDt = this.hotelAndMarketService.toLocalTime(
        value.format('YYYY-MM-DD'),
      );

      // Clear an end date that now precedes the new start date.
      if (
        this.componentSeasonEndDt &&
        this.componentSeasonStartDt.isAfter(this.componentSeasonEndDt)
      ) {
        this.componentSeasonEndDt = null;
      }
    } else {
      this.componentSeasonStartDt = null;
    }

    this.isDateRangeChanged = true;
  }

  <template>
    <div
      class={{styles.card}}
      data-test-id="seasonal-strategy-card"
      ...attributes
    >
      {{#if @isEditing}}
        <div class={{styles.form}} data-test-id="seasonal-strategy-editor">
          <div class="flex flex-row gap-200">
            <div>
              {{! Wrapping div avoids incorrect DatepickerFormField rendering (older component). }}
              <GeneralFormsFormFieldsDatepickerFormField
                {{! @glint-expect-error: Incorrect type }}
                @datePickerMaxDate={{this.maxAllowedDatepickerEndDate}}
                @datePickerMinDate={{this.minAllowedDatepickerStartDate}}
                @isInErrorState={{and
                  this.hasExactOverlap
                  this.isDateRangeChanged
                }}
                @isInWarningState={{and
                  this.hasPartialOverlapWarning
                  this.isDateRangeChanged
                }}
                @isTouched={{this.isDatepickerTouched}}
                @label={{t "events.start_date"}}
                @onSelectDate={{this.updateStartDate}}
                @removePadding={{true}}
                @requiredErrorMessage={{t
                  "pricing_assistant.recommendations.seasonal_strategy.start_date_required_error"
                }}
                @selectedDate={{this.componentSeasonStartDt}}
                {{! @glint-expect-error: Incorrect type }}
                class="date-picker__form-field--small mr-200"
              />
            </div>
            <div>
              {{! Wrapping div avoids incorrect DatepickerFormField rendering (older component). }}
              <GeneralFormsFormFieldsDatepickerFormField
                {{! @glint-expect-error: Incorrect type }}
                @datePickerMaxDate={{this.maxAllowedDatepickerEndDate}}
                @datePickerMinDate={{this.minAllowedDatepickerEndDate}}
                @isInErrorState={{and
                  this.hasExactOverlap
                  this.isDateRangeChanged
                }}
                @isInWarningState={{and
                  this.hasPartialOverlapWarning
                  this.isDateRangeChanged
                }}
                @isTouched={{this.isDatepickerTouched}}
                @label={{t "events.end_date"}}
                @onSelectDate={{this.updateEndDate}}
                @removePadding={{true}}
                @requiredErrorMessage={{t
                  "pricing_assistant.recommendations.seasonal_strategy.end_date_required_error"
                }}
                @selectedDate={{this.componentSeasonEndDt}}
                {{! @glint-expect-error: Incorrect type }}
                class="date-picker__form-field--small"
              />
            </div>
          </div>

          <div class="flex flex-col">
            <PrismInput
              @hasErrors={{if this.saveAttempted (not this.isNameValid)}}
              @label={{t "general.name"}}
              @onInput={{this.updateName}}
              @removePadding={{true}}
              @value={{@strategy.name}}
              data-test-field="Name"
            />
          </div>

          {{#if this.hasValidationMessages}}
            <div
              class="text-200--medium text-critical-default flex flex-col gap-100"
              data-test-id="seasonal-strategy-editor-messages"
            >
              {{#unless this.isNameValid}}
                <div class="flex flex-row items-center gap-100">
                  <PrismIcon
                    @color="critical"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  {{t
                    "pricing_assistant.recommendations.seasonal_strategy.name_required_error"
                  }}
                </div>
              {{/unless}}

              {{#if this.isNameUsed}}
                <div class="flex flex-row items-center gap-100">
                  <PrismIcon
                    @color="critical"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  {{t
                    "pricing_assistant.recommendations.seasonal_strategy.name_used_error"
                  }}
                </div>
              {{/if}}

              {{#if this.exactlyOverlappingSeasonalStrategy}}
                <div class="flex flex-row items-center gap-100">
                  <PrismIcon
                    @color="critical"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  {{t
                    "pricing_assistant.recommendations.seasonal_strategy.exact_overlap_error"
                    strategyName=this.exactlyOverlappingSeasonalStrategy.name
                  }}
                </div>
              {{/if}}

              {{#if
                (and this.hasPartialOverlapWarning this.isDateRangeChanged)
              }}
                <div
                  class="text-warning-default flex flex-row items-center gap-100"
                  data-test-id="seasonal-strategy-editor-warning"
                >
                  <PrismIcon
                    @color="warning"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  {{t
                    "pricing_assistant.recommendations.seasonal_strategy.days_overlap.warning"
                  }}
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{! TODO(INDU-1678): render the flat per-room offset table here. }}

          <div class="flex flex-row justify-end gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{this.saveSeasonalStrategyTask.isRunning}}
              @label={{t "general.cancel"}}
              @onClick={{this.onCancel}}
              data-test-button="Cancel"
            />
            <PrismButton
              @isLoading={{this.saveSeasonalStrategyTask.isRunning}}
              @label={{t "general.save"}}
              @onClick={{this.saveSeasonalStrategyTask.perform}}
              data-test-button="Save"
            />
          </div>
        </div>
      {{else}}
        <div class={{styles.header}}>
          <div class="flex flex-col">
            <div class="flex flex-row items-center gap-200">
              <p
                class="text-200--regular"
                data-test-id="seasonal-strategy-date-range"
              >
                {{this.dateRangeTitle}}
              </p>
              {{#if this.isOverlapped}}
                <div data-test-id="seasonal-strategy-overlap-badge">
                  <PrismBadge @iconName="warning" @type="warning">
                    {{t
                      "pricing_assistant.recommendations.seasonal_strategy.days_overlap.label"
                    }}
                    <PrismTooltip
                      @side="bottom"
                      @text={{t
                        "pricing_assistant.recommendations.seasonal_strategy.days_overlap.text"
                      }}
                    />
                  </PrismBadge>
                </div>
              {{/if}}
            </div>
            <h3 data-test-id="seasonal-strategy-name">{{@strategy.name}}</h3>
          </div>

          <div class="flex flex-row items-center gap-100">
            {{#unless @isReadOnly}}
              <PrismButton
                @buttonType="ghost"
                @icon="edit-pencil"
                @onClick={{this.edit}}
                data-test-id="edit-seasonal-strategy-button"
              />
              <PrismButton
                @buttonType="ghost"
                @icon="bin"
                @onClick={{@onDelete}}
                data-test-id="delete-seasonal-strategy-button"
              />
            {{/unless}}
            <PrismButton
              @buttonType="ghost"
              @icon={{if this.isOpen "chevron-up" "chevron-down"}}
              @onClick={{this.toggleOpen}}
              data-test-id="toggle-seasonal-strategy-details-button"
            />
          </div>
        </div>

        {{#if this.isOpen}}
          <div
            class={{styles.details}}
            data-test-id="seasonal-strategy-card-details"
          >
            {{! TODO(INDU-1678): read-only per-room offsets go here. }}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
